/**
 * Partner Edit Page
 * 
 * Full editing capability for all partner fields.
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { logger } from '@/app/utils/logger';
import { Card, CardHeader, CardBody } from '@/components/common/Card';
import { Input, Select, TextArea } from '@/components/common/FormField';
import {
  PartnerResponse,
  UpdatePartnerRequest,
  DefaultOfferConfig,
  PartnerContentOverrides,
  PartnerConfigJson,
  PartnerType,
  PARTNER_TYPE_OPTIONS,
  TIMEZONE_OPTIONS,
  localDatetimeToUtcIso,
  utcIsoToLocalDatetime,
} from '@/app/types/partners';
import { partnerService } from '@/app/services/partnerService';
import { auditService } from '@/app/services';
import { DefaultOffersEditor } from '@/components/partners/DefaultOffersEditor';
import { ContentOverridesEditor } from '@/components/partners/ContentOverridesEditor';
import { usePartnerData } from '@/app/hooks/usePartnerData';

import './PartnerEdit.scss';

export const PartnerEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { invalidate, refresh } = usePartnerData();

  // Loading state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [formData, setFormData] = useState<UpdatePartnerRequest>({});
  const [originalData, setOriginalData] = useState<PartnerResponse | null>(null);
  const [schedActivationLocal, setSchedActivationLocal] = useState('');
  const [schedActivationTimezone, setSchedActivationTimezone] = useState(
    TIMEZONE_OPTIONS[0]?.value ?? 'America/New_York'
  );

  // Fetch partner data
  useEffect(() => {
    const fetchPartner = async () => {
      if (!id) return;
      
      setLoading(true);
      setError(null);
      
      try {
        const partner = await partnerService.getPartnerById(parseInt(id, 10));
        if (!partner) {
          setError('Partner not found');
          return;
        }
        setOriginalData(partner);
        setFormData({
          partner_id: partner.partner_id,
          partner_name: partner.partner_name,
          doing_business_as: partner.doing_business_as,
          partner_type: partner.partner_type,
          program_code: partner.program_code ?? null,
          package_id: partner.package_id ?? null,
          purse_name: partner.purse_name ?? null,
          earned_benefits: partner.earned_benefits,
          default_offers: partner.default_offers,
          content_overrides: partner.content_overrides,
          effective_date: partner.effective_date,
          expiration_date: partner.expiration_date,
          is_active: partner.is_active,
          notes: partner.notes,
          config: partner.config ?? { fulfillment_enabled: true },
        });
        if (partner.scheduled_activation_at && partner.activation_timezone) {
          setSchedActivationLocal(
            utcIsoToLocalDatetime(
              partner.scheduled_activation_at,
              partner.activation_timezone
            )
          );
          setSchedActivationTimezone(partner.activation_timezone);
        } else {
          setSchedActivationLocal('');
          setSchedActivationTimezone(TIMEZONE_OPTIONS[0]?.value ?? 'America/New_York');
        }
      } catch (err) {
        setError('Failed to load partner');
        logger.error('Failed to load partner for editing', { error: err, partnerId: id });
      } finally {
        setLoading(false);
      }
    };

    fetchPartner();
  }, [id]);

  // Handle form field changes
  const handleChange = <K extends keyof UpdatePartnerRequest>(
    field: K,
    value: UpdatePartnerRequest[K]
  ) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Handle default offers changes
  const handleDefaultOffersChange = (offers: DefaultOfferConfig[] | null) => {
    setFormData(prev => ({ ...prev, default_offers: offers }));
  };

  // Handle content overrides changes
  const handleContentOverridesChange = (overrides: PartnerContentOverrides | null) => {
    setFormData(prev => ({ ...prev, content_overrides: overrides }));
  };

  // Save changes
  const handleSave = async () => {
    if (!id) return;
    
    setSaving(true);
    setError(null);
    
    try {
      const schedulePayload =
        formData.is_active === false && schedActivationLocal.trim()
          ? {
              scheduled_activation_at: localDatetimeToUtcIso(
                schedActivationLocal,
                schedActivationTimezone
              ),
              activation_timezone: schedActivationTimezone,
            }
          : { scheduled_activation_at: null, activation_timezone: null };

      const updated = await partnerService.updatePartner(parseInt(id, 10), {
        ...formData,
        ...schedulePayload,
      });
      if (updated) {
        // Log record update for audit
        auditService.logRecordModification('updated', 'partner', id, {
          partner_name: updated.partner_name,
        }).catch((err) => logger.warn('Failed to log record update', { error: err }));
        
        invalidate();
        refresh(); // Force immediate refetch
        navigate(`/partners/${id}`);
      } else {
        setError('Failed to save changes');
      }
    } catch (err) {
      setError('Failed to save changes');
      logger.error('Failed to save partner changes', { error: err, partnerId: id });
    } finally {
      setSaving(false);
    }
  };

  // Cancel editing
  const handleCancel = () => {
    navigate(`/partners/${id}`);
  };

  if (loading) {
    return (
      <div className="partner-edit-page">
        <div className="loading-state">
          <div className="spinner" />
          <span>Loading partner...</span>
        </div>
      </div>
    );
  }

  if (error && !originalData) {
    return (
      <div className="partner-edit-page">
        <div className="error-state">
          <span className="error-icon">⚠️</span>
          <span className="error-message">{error}</span>
          <button onClick={() => navigate('/partners')} className="btn-back">
            Back to Partners
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="partner-edit-page">
      {/* Back Navigation */}
      <div className="back-nav">
        <button onClick={handleCancel} className="btn-back-link">
          ← Cancel and go back
        </button>
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="header-actions">
          <button 
            className="btn-cancel" 
            onClick={handleCancel}
            disabled={saving}
          >
            Cancel
          </button>
          <button 
            className="btn-save" 
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {error && (
        <div className="error-banner">
          <span>⚠️ {error}</span>
        </div>
      )}

      {/* Basic Information */}
      <Card className="form-card">
        <CardHeader title="Basic Information" />
        <CardBody>
          <div className="form-grid">
            <Input
              label="Partner Product ID"
              value={originalData?.partner_product_id?.toString() || ''}
              onChange={() => {}}
              type="number"
              disabled
              hint="Primary key (read-only)"
            />
            <Input
              label="Partner ID"
              value={formData.partner_id?.toString() || ''}
              onChange={(val) => handleChange('partner_id', val ? parseInt(val, 10) : null)}
              type="number"
              placeholder="Partner identifier"
            />
            <Input
              label="Partner Name"
              value={formData.partner_name || ''}
              onChange={(val) => handleChange('partner_name', val)}
              placeholder="Official partner name"
            />
            <Input
              label="Doing Business As"
              value={formData.doing_business_as || ''}
              onChange={(val) => handleChange('doing_business_as', val)}
              placeholder="DBA / Display name"
            />
            <Select
              label="Partner Type"
              value={formData.partner_type || ''}
              onChange={(val) => handleChange('partner_type', val as PartnerType)}
              options={PARTNER_TYPE_OPTIONS.filter(o => o.value !== '').map(o => ({
                value: o.value,
                label: o.label,
              }))}
            />
            <Input
              label="Program Code"
              value={formData.program_code || ''}
              onChange={(val) => handleChange('program_code', val || null)}
              placeholder="InComm program code"
              hint="Optional InComm program code for this partner"
            />
            <Input
              label="Package ID"
              value={formData.package_id || ''}
              onChange={(val) => handleChange('package_id', val || null)}
              placeholder="InComm package ID"
              hint="Optional InComm package ID for this partner"
            />
            <Input
              label="Purse Name"
              value={formData.purse_name || ''}
              onChange={(val) => handleChange('purse_name', val || null)}
              placeholder="InComm purse name"
              hint="InComm purse/benefit identifier (max 20 chars)"
            />
            <div className="checkbox-field">
              <label>
                <input
                  type="checkbox"
                  checked={formData.is_active ?? true}
                  onChange={(e) => handleChange('is_active', e.target.checked)}
                />
                <span>Active</span>
              </label>
              <span className="field-hint">
                Whether this partner configuration is currently active.
              </span>
            </div>
            {formData.is_active === false && (
              <>
                <Input
                  type="datetime-local"
                  label="Scheduled Activation"
                  value={schedActivationLocal}
                  onChange={setSchedActivationLocal}
                  hint="Optional. When set, this partner will automatically activate at the specified time."
                />
                <Select
                  label="Timezone"
                  value={schedActivationTimezone}
                  onChange={(val) => setSchedActivationTimezone(val)}
                  options={TIMEZONE_OPTIONS.map((o) => ({
                    value: o.value,
                    label: o.label,
                  }))}
                />
              </>
            )}
            <div className="checkbox-field">
              <label>
                <input
                  type="checkbox"
                  checked={(formData.config as PartnerConfigJson | undefined)?.fulfillment_enabled ?? true}
                  onChange={(e) => handleChange('config', {
                    ...((formData.config as PartnerConfigJson | undefined) ?? { fulfillment_enabled: true }),
                    fulfillment_enabled: e.target.checked,
                  })}
                />
                <span>Fulfillment Enabled</span>
              </label>
              <span className="field-hint">
                When disabled, reward redemptions are created but never exported for processing.
                Use for demo/sales accounts that should not trigger real fulfillments.
              </span>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Effective Period */}
      <Card className="form-card">
        <CardHeader title="Effective Period" />
        <CardBody>
          <div className="form-grid">
            <Input
              label="Effective Date"
              value={formData.effective_date || ''}
              onChange={(val) => handleChange('effective_date', val)}
              type="date"
              hint="When this configuration becomes effective"
            />
            <Input
              label="Expiration Date"
              value={formData.expiration_date || ''}
              onChange={(val) => handleChange('expiration_date', val || null)}
              type="date"
              placeholder="Optional"
              hint="When this configuration expires (leave empty for no expiration)"
            />
          </div>
        </CardBody>
      </Card>

      {/* Default Offers */}
      <Card className="form-card">
        <CardHeader 
          title="Default Offers" 
          subtitle="Rewards automatically assigned to new users when they first access the catalog"
        />
        <CardBody>
          <DefaultOffersEditor
            value={formData.default_offers || null}
            onChange={handleDefaultOffersChange}
          />
        </CardBody>
      </Card>

      {/* Content Overrides */}
      <Card className="form-card overrides-card">
        <CardHeader 
          title="Content Overrides" 
          subtitle="Configure partner-specific customizations for reward workflows. You can hide fields, disable validations, or provide custom content values."
        />
        <CardBody>
          <ContentOverridesEditor
            value={formData.content_overrides || null}
            onChange={handleContentOverridesChange}
          />
        </CardBody>
      </Card>

      {/* Notes */}
      <Card className="form-card">
        <CardHeader title="Notes" />
        <CardBody>
          <TextArea
            label="Notes"
            value={formData.notes || ''}
            onChange={(val) => handleChange('notes', val || null)}
            placeholder="Optional notes about this partner configuration"
            rows={4}
          />
        </CardBody>
      </Card>

      {/* Bottom Actions */}
      <div className="bottom-actions">
        <button 
          className="btn-cancel" 
          onClick={handleCancel}
          disabled={saving}
        >
          Cancel
        </button>
        <button 
          className="btn-save" 
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
};

export default PartnerEdit;

